import React, {
  Fragment,
  // useId
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import classNames from "classnames";
import MarkdownIt from "markdown-it";
import moment from "moment";

import {
  PENDING_REVIEW,
  ACCEPTED,
  map as evidenceStatusMap,
  DISMISSED,
} from "../EvidenceStatus";

import {
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Fab,
  Icon,
  Tooltip,
} from "@mui/material";
import { viVN } from "@mui/x-date-pickers/locales";

const markdown = new MarkdownIt();

const IncidentEvidenceItemVideo = forwardRef(function IncidentEvidenceItemVideo(
  {
    attachment,
    updateEvidence,
    editable,
    classes,
    synchronisedPlayback,
    isSyncMaster,
    onMasterTimeUpdate,
    onUserInitiatedPlay,
    onUserInitiatedPause,
  },
  ref
) {
  // const id = useId();
  const videoRef = useRef(null);
  const [updating, setUpdating] = useState(false);
  const [playing, setPlaying] = useState(
    () => videoRef.current?.playing || false
  );

  const [supressEmitEvent, setSupressEmitEvent] = useState(false);

  useImperativeHandle(
    ref,
    () => {
      return {
        play() {
          // console.log("requested play video", {
          //   videoId: videoRef.current.id,
          //   playing,
          // });

          setSupressEmitEvent(true);
          videoRef.current
            .play()
            .finally(() => {
              setSupressEmitEvent(false);
            })
            .catch((err) => {
              console.log("Error playing video:", err);
            });
        },
        mute() {
          videoRef.current.muted = true;
        },
        pause() {
          // console.log("requested pause video", {
          //   videoId: videoRef.current.id,
          //   playing,
          // });
          videoRef.current.pause();
        },
        currentTime(val) {
          if (!val) return videoRef.current.currentTime;

          setSupressEmitEvent(true);
          videoRef.current
            .play()
            .finally(() => {
              setSupressEmitEvent(false);
            })
            .catch((err) => {
              console.log("Error playing video:", err);
            });

          const maxDelta = 1;
          const delta = videoRef.current.currentTime - val;
          if (delta > maxDelta || delta < -maxDelta) {
            videoRef.current.currentTime = val;
          }
        },
      };
    },
    []
  );

  const setEvidenceStatus = (status) => {
    setUpdating(true);

    return updateEvidence({
      key: attachment.key,
      values: {
        description: attachment.description,
        status,
      },
    })
      .then(() => setUpdating(false))
      .catch((error) => {
        console.error(error);
        setUpdating(false);
      });
  };

  const handleAcceptClick = () => {
    return setEvidenceStatus(ACCEPTED);
  };

  const handleDismissClick = () => {
    return setEvidenceStatus(DISMISSED);
  };

  const handlePlay = (event) => {
    setPlaying(true);
    // console.log("playing", { videoId: event.target.id });
    !supressEmitEvent && onUserInitiatedPlay();
  };

  const handlePause = (event) => {
    // console.log("paused", { videoId: event.target.id });
    // if (event.target.paused) return;

    setPlaying(false);

    if (!event.target.ended) onUserInitiatedPause();
  };

  const handleTimeUpdate = (event) => {
    // console.log("time update", { videoId: event.target.id, supressEmitEvent });

    if (isSyncMaster) onMasterTimeUpdate(event.target.currentTime);
  };

  // const handleTimeSeeking = (event) => {
  //   // console.log("seeking", { videoId: event.target.id, supressEmitEvent });
  //   //   if (!syncPlayFootage) return;
  //   //   const currentTime = event.target.currentTime;
  //   //   var startAtTime = moment(attachment.startAt).add(currentTime, "seconds");
  //   //   setMasterCurrentTimestamp(startAtTime.format());
  //   //   setMasterCurrentTime(currentTime);
  // };

  return (
    <Card
      className={classes.card}
      elevation={isSyncMaster ? 24 : playing ? undefined : undefined}
      sx={{
        color: isSyncMaster
          ? "info.contrastText"
          : playing
          ? "info.contrastText"
          : undefined,
        backgroundColor: isSyncMaster
          ? "info.main"
          : playing
          ? "info.main"
          : undefined,
      }}
    >
      {attachment.type === "PICTURE" && (
        <CardMedia
          onClick={() => window.open(attachment.source.src)}
          className={classNames(classes.media, classes.mediaImage)}
          image={attachment.source.src}
        />
      )}
      {attachment.type === "VIDEO" && (
        <Fragment>
          {/* <small>
            <pre>
              Current time: {videoRef.current?.currentTime}
              <br />
              Start: {attachment.startAt} ({moment(attachment.startAt).unix()})
              <br />
              End: {attachment.endAt} ({moment(attachment.endAt).unix()})
              <br />
              Duration:{" "}
              {moment(attachment.endAt).unix() -
                moment(attachment.startAt).unix()}
            </pre>
          </small>
          <br /> */}
          {attachment.source.contentType === "video/webm" ||
          attachment.source.contentType === "video/mp4" ? (
            <CardContent className={classes.mediaVideo}>
              <video
                id={attachment.index}
                className={classes.html5Video}
                controls
                playsInline
                ref={videoRef}
                onPlay={handlePlay}
                onPause={handlePause}
                // onSeeking={handleTimeSeeking}
                onTimeUpdate={handleTimeUpdate}
              >
                Sorry, your browser does not support embedded videos
                <source
                  src={attachment.source.src}
                  type={attachment.source.contentType}
                />
              </video>
            </CardContent>
          ) : (
            <CardContent className={classes.videoDownload}>
              <div>
                <Icon className={classes.videoCam}>videocam</Icon>
              </div>
              <div>
                <Button
                  size="small"
                  component="a"
                  target="_blank"
                  href={attachment.source.src}
                >
                  <Icon className={classes.leftIcon}>cloud_download</Icon>
                  Download
                </Button>
              </div>
            </CardContent>
          )}
        </Fragment>
      )}
      <CardContent sx={{ position: "relative" }}>
        {attachment.status === PENDING_REVIEW && editable && (
          <>
            <Tooltip
              title="Keep this evidence as part of the incident"
              enterDelay={300}
            >
              <Button
                color="success"
                size="small"
                variant="contained"
                disabled={updating}
                onClick={handleAcceptClick}
                sx={{ marginRight: 2 }}
              >
                Keep
              </Button>
            </Tooltip>
            <Tooltip
              title="This evidence is not relevant - it can be retrieved later if needed"
              enterDelay={300}
            >
              <Button
                color="error"
                size="small"
                variant="contained"
                disabled={updating}
                onClick={handleDismissClick}
              >
                Dismiss
              </Button>
            </Tooltip>
          </>
        )}
        {(attachment.status !== PENDING_REVIEW || !editable) && (
          <Chip
            label={evidenceStatusMap(attachment.status)}
            className={classNames(classes.chip, {
              [classes.acceptedChip]: attachment.status === ACCEPTED,
              [classes.pendingReviewChip]: attachment.status === PENDING_REVIEW,
            })}
          />
        )}
        {attachment.description && (
          <div
            dangerouslySetInnerHTML={{
              __html: markdown.render(attachment.description),
            }}
          />
        )}
        {playing && synchronisedPlayback && (
          <Icon
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              mb: 1,
              mr: 1,
            }}
          >
            link
          </Icon>
        )}
      </CardContent>
    </Card>
  );
});

export default IncidentEvidenceItemVideo;
